import React, { useState } from "react";
import { Box, Popover } from "@mui/material";
import { KeyboardArrowDownRounded } from "@mui/icons-material";
import type { PaymentStatus } from "../../../types/bookings";

const PAYMENT_STATUS_OPTIONS: PaymentStatus[] = [
  "pending",
  "paid",
  "sponsored",
  "other",
];

const getPaymentStatusChipStyles = (status: PaymentStatus) => {
  switch (status) {
    case "pending":
      return {
        backgroundColor: "#FFF3CD",
        color: "#856404",
        label: "Pending",
      };

    case "paid":
      return {
        backgroundColor: "#D4EDDA",
        color: "#155724",
        label: "Paid",
      };

    case "sponsored":
      return {
        backgroundColor: "#D1ECF1",
        color: "#0C5460",
        label: "Sponsored",
      };

    case "other":
      return {
        backgroundColor: "#E2E3E5",
        color: "#383D41",
        label: "Other",
      };

    default:
      return {
        backgroundColor: "#E0E0E0",
        color: "#424242",
        label: status,
      };
  }
};

interface PaymentStatusDropdownProps {
  bookingId: number;
  currentStatus: PaymentStatus;
  onPaymentStatusChange?: (id: number, newStatus: PaymentStatus) => void;
}

const PaymentStatusDropdown: React.FC<PaymentStatusDropdownProps> = ({
  bookingId,
  currentStatus,
  onPaymentStatusChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const handleSelect = (e: React.MouseEvent, status: PaymentStatus) => {
    e.stopPropagation();
    onPaymentStatusChange?.(bookingId, status);
    setAnchorEl(null);
  };

  const currentStyles = getPaymentStatusChipStyles(currentStatus);

  return (
    <>
      {/* Trigger Chip */}
      <Box
        onClick={handleOpen}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          px: "12px",
          py: "5px",
          borderRadius: "999px",
          backgroundColor: currentStyles.backgroundColor,
          color: currentStyles.color,
          cursor: "pointer",
          fontWeight: 600,
          fontSize: "12px",
          userSelect: "none",
          transition: "filter 0.15s ease",
          "&:hover": {
            filter: "brightness(0.95)",
          },
        }}
      >
        {currentStyles.label}

        <KeyboardArrowDownRounded
          sx={{
            fontSize: "16px",
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      {/* Dropdown */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(e) => handleClose(e as React.MouseEvent)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              mt: "6px",
              borderRadius: "16px",
              p: "10px",
              minWidth: "180px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        {PAYMENT_STATUS_OPTIONS.map((status) => {
          const styles = getPaymentStatusChipStyles(status);
          const isSelected = status === currentStatus;

          return (
            <Box
              key={status}
              onClick={(e) => handleSelect(e, status)}
              sx={{
                px: "16px",
                py: "8px",
                borderRadius: "999px",
                backgroundColor: styles.backgroundColor,
                color: styles.color,
                fontWeight: isSelected ? 700 : 500,
                fontSize: "13px",
                textAlign: "center",
                cursor: "pointer",
                outline: isSelected ? `2px solid ${styles.color}` : "none",
                outlineOffset: "-2px",
                transition: "filter 0.15s ease, transform 0.1s ease",
                "&:hover": {
                  filter: "brightness(0.93)",
                  transform: "scale(1.02)",
                },
              }}
            >
              {styles.label}
            </Box>
          );
        })}
      </Popover>
    </>
  );
};

export default PaymentStatusDropdown;
